/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import * as actionTypes from '../constants/action-types';
import UniversalTrackerModalService from '../model/UniversalTrackerModalService';
import { apiClient, handleTokenAuth } from './api';
import { API } from '../constants/errors';
import { createUniversalTrackerValue } from './universalTrackerValue';
import { handleError } from './error';
import { UtrvType } from '../constants/status';
import UniversalTracker, { UniversalTrackerType } from '../model/UniversalTracker';
import { ValueData } from '../types/surveyScope';
import { AppDispatch, RootState } from '../reducers';
import { NewUniversalTrackerValuePlain } from '../model/UniversalTrackerValue';
import { withProfile } from '../utils/files';
import { DataFormState } from '../components/utr-modal/components/SetDataForm';
import { ShowAs } from '../reducers/universal-tracker-modal';

export interface UniversalTrackerModalProps {
  universalTrackerId: string;
  universalTrackerType?: UniversalTrackerType | undefined;
  actionValueId?: string | undefined;
  params?: { surveyId: string };
  openAction?: { activeTabId?: ShowAs, hideProvenance?: boolean };
  alternativeCode?: string;
}

interface FormData {
  value: any;
  effectiveDate?: string;
  metricUnit?: any;
  type?: UtrvType;
  universalTracker?: UniversalTracker;
  files?: any[];
  sampleSize?: number | boolean;
  valueData?: ValueData;
  note?: string;
}

export function loadUniversalTrackerModal(props: UniversalTrackerModalProps) {
  const {
    universalTrackerId,
    universalTrackerType = UniversalTrackerType.Utr,
    actionValueId,
    params,
    openAction,
    alternativeCode,
  } = props;
  return (dispatch: AppDispatch, getState: () => RootState) => {
    if (!universalTrackerId) {
      return;
    }
    const modalState = getState().universalTrackerModal;
    const isSameDispatch = modalState.universalTrackerId === universalTrackerId &&
      modalState.universalTrackerType === universalTrackerType;

    if (isSameDispatch) {
      if (modalState.loadedOverview && !modalState.open) {
        dispatch({
          ...openAction,
          type: actionTypes.OPEN_UNIVERSALTRACKER_MODAL,
          alternativeCode,
        });
      }
      return;
    }

    dispatch({
      ...openAction,
      type: actionTypes.LOADING_UNIVERSALTRACKER_MODAL,
      universalTrackerType: universalTrackerType,
      universalTrackerId: universalTrackerId,
      alternativeCode,
    });

    const options = handleTokenAuth(getState);
    if (params) {
      options.params = params;
    }
    let apiRequest;
    switch (universalTrackerType) {
      case 'frameworks':
        apiRequest = apiClient.get('framework-utrs/' + universalTrackerId + '/modal/info', options);
        break;
      case 'utr':
      default:
        // If we have utrv id, fetch extended data
        if (actionValueId) {
          apiRequest = apiClient.get('universal-tracker-values/' + actionValueId + '/modal/info', options)
        } else {
          apiRequest = apiClient.get('universal-trackers/' + universalTrackerId + '/modal/info', options);
        }
        break;
    }

    apiRequest
      .then(response => {
        const modalService: UniversalTrackerModalService = new UniversalTrackerModalService(
          response.data.data,
          universalTrackerType
        );
        if (actionValueId) {
          modalService.setCurrentValueId(actionValueId);

          // Set up extra data
          if (universalTrackerType === 'utr') {
            const utrvData = response.data.data.universalTrackerValues;
            if (utrvData.length === 1) {
              modalService.setCurrentValue(utrvData[0]);
              modalService.setInitiativeId(utrvData[0].initiativeId);
            }
          }
        }

        if (!modalService.getInitiativeId()) {
          modalService.setInitiativeId(getState().initiative.data?._id);
        }

        switch (universalTrackerType) {
          case 'frameworks':
            dispatch({
              type: actionTypes.LOADED_HISTORY_UNIVERSALTRACKER_MODAL,
              modalService: modalService
            });
            break;
          case 'utr':
          default:
            dispatch(loadHistoryByModalService(modalService));
            break;
        }

        dispatch({
          type: actionTypes.LOADED_INFO_UNIVERSALTRACKER_MODAL,
          modalService: modalService
        });

      })
      .catch(handleError(dispatch, actionTypes.ERROR_UNIVERSALTRACKER_MODAL));
  }
}

export function loadHistoryByModalService(modalService: UniversalTrackerModalService) {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const modalState = getState().universalTrackerModal;
    if (modalState.loadingHistory || modalService.universalTrackerValuesLoaded()) {
      return;
    }

    dispatch({ type: actionTypes.LOADING_HISTORY_UNIVERSALTRACKER_MODAL });

    let initiativeId: string | undefined = modalService.getInitiativeId();
    if (!initiativeId) {
      initiativeId = getState().initiative.data?._id;
    }

    if (initiativeId === undefined) {
      return;
    }

    const options = handleTokenAuth(getState);
    const universalTracker = modalService.getUniversalTracker();
    if (universalTracker.isFramework()) {
      console.log('Unexpected history call for framework');
      return
    }

    apiClient.get(
      `universal-trackers/${universalTracker.getId()}/history/initiativeId/${initiativeId}`,
      options
    )
      .then(response => {
        modalService.setUniversalTrackerValues(response.data.data);
        dispatch({
          type: actionTypes.LOADED_HISTORY_UNIVERSALTRACKER_MODAL,
          initiativeId: initiativeId,
          modalService: modalService,
          data: response.data.data,
        });
      })
      .catch(handleError(dispatch, actionTypes.ERROR_UNIVERSALTRACKER_MODAL));
  }
}

export function reloadHistory(modalService: UniversalTrackerModalService) {
  modalService.setUniversalTrackerValues(undefined); // Clear cache
  return loadHistoryByModalService(modalService);
}

// Used by public outstanding actions endpoint
export function loadByModalService(modalService: UniversalTrackerModalService) {
  return (dispatch: AppDispatch) => {
    const universalTracker = modalService.getUniversalTracker();
    dispatch({
      type: actionTypes.LOADING_UNIVERSALTRACKER_MODAL,
      universalTrackerType: universalTracker.getType(),
      universalTrackerId: universalTracker.getId()
    });

    dispatch({
      type: actionTypes.LOADED_INFO_UNIVERSALTRACKER_MODAL,
      modalService: modalService
    });

    if (!universalTracker.isFramework()) {
      dispatch(loadHistoryByModalService(modalService))
    }
  }
}

export function closeUniversalTrackerModal() {
  return (dispatch: AppDispatch) => dispatch({ type: actionTypes.CLOSE_UNIVERSALTRACKER_MODAL })
}

function getFormData(form: DataFormState, additionalFields = {}) {
  // Merge additional fields first, then convert to formData
  const formData: FormData = {
    ...additionalFields,
    value: form.value,
    valueData: form.valueData
  };

  if ([UtrvType.Baseline, UtrvType.Target].includes(form.type)) {
    formData.type = form.type;
  }

  if (form.files && form.files.length > 0) {
    formData.files = [];
    form.files.forEach((file: any) => {
      formData.files?.push(file.file);
    });
  }

  if (form.sampleSize !== undefined) {
    formData.sampleSize = form.sampleSize;
  }

  if (form.comments !== undefined) {
    formData.note = form.comments;
  }

  if (form.effectiveDate !== undefined) {
    formData.effectiveDate = form.effectiveDate;
  }

  return withProfile(formData);
}

export function createUtrvByModalService(form: DataFormState, universalTrackerValue: NewUniversalTrackerValuePlain) {
  const formData = getFormData(form, universalTrackerValue);

  return async (dispatch: AppDispatch, getState: () => RootState) => {

    const saveWrapper = createUniversalTrackerValue(formData);
    const savePromise = saveWrapper(dispatch, getState);

    return savePromise.then((result) => {

      // TODO - make this only update one action rather than full refresh...
      // dispatch(loadUserActions());
      // dispatch(reloadScorecards());
      dispatch({ type: actionTypes.CLOSE_UNIVERSALTRACKER_MODAL });

      return result;
    }).catch(e => {
      console.log(e);
      throw new Error(e.response ? API.GENERIC : e.message);
    });
  }
}
